import { useEffect } from "react";
import {
  LOGO,
  OG_IMAGE,
  OG_IMAGE_ALT,
  ORIGIN,
  SAME_AS,
  SITE_NAME,
  SITE_SUMMARY,
  pageMeta,
  type PageMeta,
} from "./site";

const ORG_ID = `${ORIGIN}/#organization`;
const WEBSITE_ID = `${ORIGIN}/#website`;
const JSONLD_ID = "seo-jsonld";

export function pageUrl(path: string): string {
  return path === "/" ? `${ORIGIN}/` : `${ORIGIN}${path}`;
}

function breadcrumb(page: PageMeta) {
  const trail: PageMeta[] = [pageMeta("/")];
  if (page.path.startsWith("/products/")) trail.push(pageMeta("/platform"));
  if (page.path !== "/") trail.push(page);
  return {
    "@type": "BreadcrumbList",
    "@id": `${pageUrl(page.path)}#breadcrumb`,
    itemListElement: trail.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.label,
      item: pageUrl(p.path),
    })),
  };
}

/** schema.org graph for a page: site-wide entities plus the page itself. */
export function jsonLd(page: PageMeta): object {
  const url = pageUrl(page.path);
  const graph: object[] = [
    {
      "@type": "Organization",
      "@id": ORG_ID,
      name: SITE_NAME,
      url: `${ORIGIN}/`,
      logo: LOGO,
      description: SITE_SUMMARY,
      sameAs: SAME_AS,
    },
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      name: SITE_NAME,
      url: `${ORIGIN}/`,
      publisher: { "@id": ORG_ID },
    },
    {
      "@type": page.kind === "company" ? "ContactPage" : "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: page.title,
      description: page.description,
      isPartOf: { "@id": WEBSITE_ID },
      breadcrumb: { "@id": `${url}#breadcrumb` },
      ...(page.kind === "product" && {
        mainEntity: {
          "@type": "Service",
          name: page.label,
          description: page.description,
          provider: { "@id": ORG_ID },
          serviceType: "CI/CD for Android Automotive software",
          url,
        },
      }),
    },
    breadcrumb(page),
  ];
  return { "@context": "https://schema.org", "@graph": graph };
}

const escapeAttr = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/** Static <head> tags for a page, injected by the build-time prerender. */
export function headHtml(page: PageMeta): string {
  const url = pageUrl(page.path);
  const t = escapeAttr(page.title);
  const d = escapeAttr(page.description);
  const alt = escapeAttr(OG_IMAGE_ALT);
  // "<" can't appear raw inside a JSON-LD <script>
  const ld = JSON.stringify(jsonLd(page)).replace(/</g, "\\u003c");
  return [
    `<title>${t}</title>`,
    `<meta name="description" content="${d}" />`,
    page.noindex
      ? `<meta name="robots" content="noindex, follow" />`
      : `<link rel="canonical" href="${url}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:locale" content="en_US" />`,
    `<meta property="og:title" content="${t}" />`,
    `<meta property="og:description" content="${d}" />`,
    `<meta property="og:url" content="${url}" />`,
    `<meta property="og:image" content="${OG_IMAGE}" />`,
    `<meta property="og:image:type" content="image/jpeg" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="${alt}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${t}" />`,
    `<meta name="twitter:description" content="${d}" />`,
    `<meta name="twitter:image" content="${OG_IMAGE}" />`,
    `<meta name="twitter:image:alt" content="${alt}" />`,
    `<script type="application/ld+json" id="${JSONLD_ID}">${ld}</script>`,
  ].join("\n    ");
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string | null) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(page: PageMeta) {
  let el = document.getElementById(JSONLD_ID);
  if (!el) {
    el = document.createElement("script");
    el.id = JSONLD_ID;
    el.setAttribute("type", "application/ld+json");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(jsonLd(page));
}

/**
 * Keeps <head> in sync on client-side navigation. First loads already carry
 * the right tags from the prerendered HTML; this covers in-app route changes.
 */
export function useSeo(path: string) {
  useEffect(() => {
    const page = pageMeta(path);
    const url = pageUrl(page.path);
    document.title = page.title;
    setMeta("name", "description", page.description);
    setMeta("name", "robots", page.noindex ? "noindex, follow" : "index, follow");
    setMeta("property", "og:title", page.title);
    setMeta("property", "og:description", page.description);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", page.title);
    setMeta("name", "twitter:description", page.description);
    setCanonical(page.noindex ? null : url);
    setJsonLd(page);
  }, [path]);
}

